import { Card, CardBody, Flex, Stack, Text, useBreakpointValue } from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { DateTime, Encoder, Grade, Status, Team } from 'components/match-stream-cards/stubs'
import ProgressDays from 'components/progress-days'
import { getMatchStreamStatus } from 'utils/stream-utils'

import theme from 'theme'

import type { StreamMatchPair } from 'components/week-display/week-display'
import type { MatchDay, MatchStatusType, Stream } from 'types'

type FooterProps = {
  stream: Stream
  matchDay?: MatchDay
  note?: string
  isActionable?: boolean
  isOrphaned?: boolean
  isMoved?: boolean
  isHistorical?: boolean
  matchDisplayStatus?: MatchStatusType
  actionableColor?: string
  onAction?: () => void
}

const CardFooter = ({
  stream,
  matchDay,
  note,
  isActionable,
  isOrphaned,
  isMoved,
  isHistorical,
  matchDisplayStatus,
  actionableColor,
  onAction,
}: FooterProps) => {
  const [hasCopied, setHasCopied] = useState(false)
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => setHasCopied(true))
    const timer = setTimeout(() => setHasCopied(false), 2000)
    return () => clearTimeout(timer)
  }
  const bgColour =
    isOrphaned && !isHistorical
      ? theme.colors.red['50']
      : isMoved && !isHistorical
        ? theme.colors.orange['50']
        : isActionable
          ? theme.colors.blue['50']
          : theme.colors.gray['200']
  const isMobile = useBreakpointValue({ base: true, md: false })

  const renderLeft = () =>
    isActionable &&
    matchDisplayStatus !== 'upcoming' && (
      <Stack direction="row" spacing={1} alignItems="center">
        <FontAwesomeIcon icon={['far', 'clone']} size="sm" color={actionableColor} />
        <Text
          textStyle="button2"
          cursor="pointer"
          color={actionableColor}
          onClick={() => copyToClipboard(stream.streamUrl ?? matchDay?.videoURL ?? '')}
        >
          {hasCopied ? 'URL Copied!' : 'Copy YouTube URL'}
        </Text>
      </Stack>
    )

  const renderRight = () =>
    isActionable && (
      <Stack direction="row" spacing={1} alignItems="center" onClick={onAction}>
        <Text textStyle="button2" cursor="pointer" color={actionableColor}>
          {matchDisplayStatus === 'orphaned' ? 'Delete Stream' : isHistorical ? 'View' : 'Manage'}
        </Text>
        <FontAwesomeIcon icon={['far', isOrphaned ? 'trash-can' : 'chevron-right']} size="sm" color={actionableColor} />
      </Stack>
    )

  const renderMiddle = () =>
    note && (
      <Text
        textAlign="center"
        textStyle={{ base: 'caption', sm: 'body2' }}
        color={theme.baseColors.text.default}
        isTruncated
      >
        {note}
      </Text>
    )

  return isMobile ? (
    <Stack spacing={0}>
      {isActionable && (
        <Flex
          h="36px"
          px="16px"
          alignItems="center"
          justifyContent={matchDisplayStatus !== 'upcoming' ? 'space-between' : 'flex-end'}
          bgColor={bgColour}
        >
          {renderLeft()}
          {renderRight()}
        </Flex>
      )}
      {note && (
        <Flex h="36px" px="16px" alignItems="center" justifyContent="center" bgColor={bgColour}>
          {renderMiddle()}
        </Flex>
      )}
    </Stack>
  ) : (
    <Flex h="36px" px="16px" alignItems="center" justifyContent="space-between" bgColor={bgColour}>
      <Flex flex={1}>{renderLeft()}</Flex>
      <Flex flex={1} justifyContent="center">
        {renderMiddle()}
      </Flex>
      <Flex flex={1} justifyContent="flex-end">
        {renderRight()}
      </Flex>
    </Flex>
  )
}

type Props = {
  pair: StreamMatchPair
  isActionable?: boolean
  onClick?: (stream: Stream | null) => void
  note?: string
}

const StreamCard = ({ pair, note, isActionable = false, onClick }: Props) => {
  const navigate = useNavigate()
  const { stream, match } = pair
  const isOrphaned = pair.status === 'match_deleted'
  const isMoved = pair.status === 'match_moved'
  const homeTeam = stream?.streamConfig?.compMatch?.homeTeam ?? match?.homeTeam
  const awayTeam = stream?.streamConfig?.compMatch?.awayTeam ?? match?.awayTeam
  const matchDay = match?.matchDays?.find(day => day.dayNum === (stream.instanceNumber ?? 1))
  const matchDate = matchDay?.dateTimeUTC ? new Date(matchDay.dateTimeUTC) : new Date()
  const matchTimezone = match?.tzid ?? 'UTC'

  const matchDisplayStatus = isOrphaned ? 'orphaned' : isMoved ? 'moved' : getMatchStreamStatus(matchDay, stream)
  const isHistorical =
    getMatchStreamStatus(matchDay, stream) === 'completed' ||
    (getMatchStreamStatus(matchDay, stream) === 'scheduled' && stream.scheduledEndUtc <= new Date().toISOString())
  const actionableColor =
    isOrphaned && !isHistorical
      ? theme.baseColors.danger.main
      : isMoved && !isHistorical
        ? theme.baseColors.warning.main
        : theme.baseColors.primary.main
  const streamLink = stream.streamGuid
    ? isHistorical
      ? `/history/${matchDate.getFullYear()}/${matchDate.getMonth() + 1}/${stream.streamGuid}`
      : `/manage/${stream.streamGuid}`
    : null

  const onAction = () => {
    if (onClick) onClick(stream ?? null)
    if (isActionable && streamLink) navigate(streamLink)
  }

  return (
    <Card w="100%">
      <CardBody p={0}>
        <Stack spacing={6} p={{ base: `12px 16px`, md: `12px 16px` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {homeTeam && <Team team={homeTeam} type="home" />}

            <Stack minW="120px" spacing="14px" justifyContent="space-between">
              {match && (
                <>
                  <Grade grade={match.gradeName} />

                  {match.matchDays && match.matchDays.length > 1 && (
                    <ProgressDays days={match.matchDays} activeDay={stream.instanceNumber ?? 1} />
                  )}
                </>
              )}
            </Stack>

            {awayTeam && <Team team={awayTeam} type="away" />}
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {matchDay && (
              <>
                {matchDay.fbDevice && (
                  <Encoder encoder={matchDay.fbDevice.displayName ?? matchDay.fbDevice.friendlyName} />
                )}

                <DateTime
                  streamDate={stream.scheduledStartUtc}
                  matchDate={matchDay.dateTimeUTC ?? ''}
                  matchDisplayStatus={matchDisplayStatus}
                  timezone={matchTimezone}
                />
              </>
            )}
            <Status status={matchDisplayStatus} />
          </Stack>
        </Stack>

        {(note || isActionable) && (
          <CardFooter
            stream={stream}
            matchDay={matchDay}
            note={note}
            isActionable={isActionable}
            isOrphaned={isOrphaned}
            isMoved={isMoved}
            isHistorical={isHistorical}
            matchDisplayStatus={matchDisplayStatus}
            actionableColor={actionableColor}
            onAction={onAction}
          />
        )}
      </CardBody>
    </Card>
  )
}

export default StreamCard
